import React, { useState, useMemo } from 'react'
import classnames from 'classnames/bind'
import * as graph from '@adc/sdk'
import { CurrentTargetAudience } from '@adc/sdk/types'
import * as blueprint from '@adc/blueprint'
import type { AuctionBlueprintOnlineReactiveArgs } from '@adc/blueprint'
import _ from 'lodash'
import styles from './styles.scss'
import Term, { TermProps } from '../term'
import Text from '../../elementals/text'
import ExpandableCard from '../../layouts/expandableCard'
import CardContainer from '../cardContainer'
import {
  GRAPH_API_FRAGMENT_DEFINITION,
  GRAPH_API_FRAGMENT_REFERENCE,
} from '../../../utils/symbols'
import { ListingBadgeVariant } from '@adc/blueprint/lib/listing'
import { AutomationTarget } from 'src/types'

const HEADER_HEIGHT = 24 + 18 * 2 + 18 // content height + padding + bottom margin
const TERM_HEIGHT = 70

const cx = classnames.bind(styles)

interface TermsCardProps extends AutomationTarget {
  title?: string
  className?: string
  initialActiveState?: boolean
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
  viewingFrom?: CurrentTargetAudience
}

const TermsCard: React.FC<TermsCardProps> = ({
  title = 'Seller Terms',
  className,
  initialActiveState = false,
  listing,
  viewingFrom = graph.enums.CurrentTargetAudience.Seller,
  elmId = 'expandable-terms-card',
}) => {
  const terms: TermProps[] = useMemo(() => {
    const badges = blueprint.listing.utils.calculatePrivateSellerBadges(
      listing,
      viewingFrom
    )
    return badges
      .filter(
        badge =>
          badge.title != 'Rentback' ||
          (badge.title === 'Rentback' && badge.content != 'None')
      )
      .map((badge: ListingBadgeVariant) => ({
        iconName: badge.titleIcon,
        label: badge.title,
        value: badge.content,
        // @ts-ignore no types
        isMoneyValue: Boolean(badge.moneyValue) ?? false,
        tooltipContent: badge.tooltipContent,
        elmId: `${elmId}-${badge.title
          .toLowerCase()
          .replace(/\s+/g, '-')}-term`,
      }))
  }, [listing, viewingFrom])

  const [active, setActive] = useState(initialActiveState)
  const slots = {
    card: ({
      elmId,
      children,
      className,
    }: {
      elmId?: string
      children?: React.ReactNode
      className?: string
    } = {}) => (
      <CardContainer
        elmId={elmId as string}
        className={className as string}
        borderRadius='medium'
        padding='small'
      >
        {children}
      </CardContainer>
    ),
    header: () => (
      <div data-elm-id={`${elmId}-header`} style={{ width: '100%' }}>
        <Text.Body size='large' weight='heavy'>
          {title}
        </Text.Body>
      </div>
    ),
    content: () => (
      <div data-elm-id={`${elmId}-terms`} className={cx('terms')}>
        {terms.map((term, index) => (
          <>
            <Term key={index} {...term} />
            {index < terms.length - 1 && <div className={cx('divider')} />}
          </>
        ))}
      </div>
    ),
  }

  return (
    <ExpandableCard
      slots={slots}
      active={active}
      onClick={() => setActive(!active)}
      classNames={{ card: cx('terms-card', className) }}
      heightHints={{
        collapsed: HEADER_HEIGHT,
        expanded: terms.length * TERM_HEIGHT + HEADER_HEIGHT,
      }}
    />
  )
}

// @ts-ignore TODO: figure out how to type this, it should be integrated into the props somehow
// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
TermsCard[GRAPH_API_FRAGMENT_DEFINITION] = `
  fragment TermsCardFields on Listing {
    seller_terms {
      finance_terms {
        finance_preference
        is_contingency
      }
      inspection_terms {
        is_contingency
        is_option_contingency
      }
      leaseback_terms {
        leaseback_period_in_days
      }
    }
    listing_configuration {
      are_private_tours_available
      days_to_close
    }
    program_configuration {
      earnest_money_deposit_minimum_amount
    }
  }
`
// @ts-ignore TODO: figure out how to type this
// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
TermsCard[GRAPH_API_FRAGMENT_REFERENCE] = '...TermsCardFields'

export default TermsCard
